import { useTheme } from '@mui/material/styles';
import { TableCell, TableRow } from '@mui/material';
import React, {
  CSSProperties,
  memo,
  MouseEventHandler,
  useCallback,
  useMemo,
} from 'react';
import { BankAccountOperationColumn } from '../../types/bank-account/BankAccountOperationColumn';
import { DraggableProvided } from '@hello-pangea/dnd';
import { BankAccountMonth } from '../../types/bank-account/BankAccountMonth';
import { GripIcon, SelectedGripIcon } from '../../assets/GripIcon';

export interface AccountMonthRowProps {
  item: BankAccountMonth;
  onColumnClick?: (column: BankAccountOperationColumn, rect: DOMRect) => void;
  onColumnDoubleClick?: (
    column: BankAccountOperationColumn,
    rect: DOMRect,
  ) => void;
  selected?: boolean;
  isDragging?: boolean;

  style?: CSSProperties;

  draggableProvided?: DraggableProvided;

  onMouseEnter?: (el: HTMLElement) => void;
  onMouseLeave?: () => void;
  onContextMenu?: (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
}

export const AccountMonthRow = memo((props: AccountMonthRowProps) => {
  const {
    item,
    style,
    onColumnClick,
    onColumnDoubleClick,
    selected,
    draggableProvided,
    onContextMenu,
    isDragging,
  } = props;

  const theme = useTheme();

  const isDarkPalette = theme.palette.mode === 'dark';
  const backgroundColor = theme.palette.grey[isDarkPalette ? 200 : 800];
  const hoverColor = theme.palette.grey[isDarkPalette ? 300 : 700];
  const textColor = theme.palette.getContrastText(backgroundColor);

  const tableRowSx = useMemo(
    () => ({
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'stretch',
      backgroundColor: selected ? hoverColor : backgroundColor,
      boxShadow: isDragging ? 4 : 0,
      ...(selected && { transition: 'background-color 200ms ease-in-out' }),
    }),
    [backgroundColor, hoverColor, isDragging, selected],
  );

  const tableCellSx = useMemo(
    () => ({
      color: textColor,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      flexBasis: 0,
      transition: 'background-color 200ms ease-in-out',
      '&:hover': {
        backgroundColor: hoverColor,
        cursor: 'pointer',
      },
    }),
    [textColor, hoverColor],
  );

  const columnClickHandler = useCallback(
    (column: BankAccountOperationColumn): MouseEventHandler<HTMLDivElement> =>
      (e) => {
        if (onColumnClick) {
          onColumnClick(column, e.currentTarget.getBoundingClientRect());
        }
      },
    [onColumnClick],
  );

  const columnDblClickHandler = useCallback(
    (column: BankAccountOperationColumn): MouseEventHandler<HTMLDivElement> =>
      (e) => {
        if (onColumnDoubleClick) {
          onColumnDoubleClick(column, e.currentTarget.getBoundingClientRect());
        }
      },
    [onColumnDoubleClick],
  );

  return (
    <TableRow
      ref={draggableProvided?.innerRef}
      component={'div'}
      sx={tableRowSx}
      {...draggableProvided?.draggableProps}
      style={style}
      onMouseEnter={(event) => {
        if (props.onMouseEnter) {
          props.onMouseEnter(event.currentTarget);
        }
      }}
      onMouseLeave={props.onMouseLeave}
      onContextMenu={onContextMenu}
    >
      <TableCell
        component={'div'}
        sx={{
          ...tableCellSx,
          padding: 0,
          flex: '0 0 auto',
          textAlign: 'center',
        }}
        {...draggableProvided?.dragHandleProps}
        onClick={columnClickHandler(BankAccountOperationColumn.DND)}
        onDoubleClick={columnDblClickHandler(BankAccountOperationColumn.DND)}
      >
        {selected ? <SelectedGripIcon /> : <GripIcon />}
      </TableCell>
      <TableCell
        component={'div'}
        sx={{
          ...tableCellSx,
          textAlign: 'center',
          fontSize: 24,
          padding: 1,
        }}
        style={{ flex: 1 }}
        onClick={columnClickHandler(BankAccountOperationColumn.MONTH_NAME)}
        onDoubleClick={columnDblClickHandler(
          BankAccountOperationColumn.MONTH_NAME,
        )}
      >
        {item.name}
      </TableCell>
    </TableRow>
  );
});
AccountMonthRow.displayName = 'AccountMonthRow';
